// frontend/src/pages/SalesGAS.js
// Group Alignment Sessions for the Sales Team Program.
// List sessions per cycle; schedule / edit sessions with multiple participants
// (EMCs, panelists, observers) and shared notes.
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getGASSessions, createGASSession, updateGASSession, deleteGASSession,
  getCycles, getUsers,
} from '../services/api';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Input, Select, Textarea,
  SectionTitle, Spinner,
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const STATUS_COLOR = { scheduled: 'blue', complete: 'green', cancelled: 'gray' };

export default function SalesGAS() {
  const { user } = useAuth();
  const isManager = ['admin','manager'].includes(user?.role);

  const [cycles,    setCycles]    = useState([]);
  const [users,     setUsers]     = useState([]);
  const [sessions,  setSessions]  = useState([]);
  const [filterCycle, setFilterCycle] = useState('');
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  const [form,      setForm]      = useState(null);   // { id?, title, cycle_id, scheduled_at, location, agenda, notes, facilitator_id, status, participants[] }

  const blankForm = () => ({
    id: null,
    title: '',
    cycle_id: filterCycle || '',
    scheduled_at: '',
    location: '',
    agenda: '',
    notes: '',
    facilitator_id: user?.id || '',
    status: 'scheduled',
    participants: [],
  });

  const load = () => Promise.all([
    getGASSessions(filterCycle || undefined).catch(() => []),
    cycles.length ? Promise.resolve(cycles) : getCycles({ program_id: 2 }).catch(() => []),
    users.length  ? Promise.resolve(users)  : getUsers().catch(() => []),
  ]).then(([s, c, u]) => {
    setSessions(Array.isArray(s) ? s : []);
    if (!cycles.length) setCycles(Array.isArray(c) ? c : []);
    if (!users.length)  setUsers(Array.isArray(u) ? u : []);
  });

  useEffect(() => {
    Promise.all([
      getCycles({ program_id: 2 }).catch(() => []),
      getUsers().catch(() => []),
    ]).then(([c, u]) => {
      const cyc = Array.isArray(c) ? c : [];
      setCycles(cyc);
      setUsers(Array.isArray(u) ? u : []);
      const active = cyc.find(x => x.status === 'active');
      if (active) setFilterCycle(String(active.id));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) {
      getGASSessions(filterCycle || undefined).then(s => setSessions(Array.isArray(s) ? s : [])).catch(() => setSessions([]));
    }
  }, [filterCycle, loading]);

  const openNew = () => setForm(blankForm());
  const openEdit = s => setForm({
    id: s.id,
    title: s.title,
    cycle_id: String(s.cycle_id),
    scheduled_at: (s.scheduled_at || '').slice(0, 16),
    location: s.location || '',
    agenda: s.agenda || '',
    notes: s.notes || '',
    facilitator_id: String(s.facilitator_id || ''),
    status: s.status,
    participants: (s.participants || []).map(p => ({
      user_id: p.user_id,
      participant_role: p.participant_role,
    })),
  });

  const toggleParticipant = (uid, role = 'panelist') => {
    setForm(f => {
      const exists = f.participants.find(p => String(p.user_id) === String(uid));
      if (exists) {
        return { ...f, participants: f.participants.filter(p => String(p.user_id) !== String(uid)) };
      }
      return { ...f, participants: [...f.participants, { user_id: Number(uid), participant_role: role }] };
    });
  };

  const setParticipantRole = (uid, role) => {
    setForm(f => ({
      ...f,
      participants: f.participants.map(p => String(p.user_id) === String(uid) ? { ...p, participant_role: role } : p),
    }));
  };

  const submit = async e => {
    e.preventDefault();
    if (!form.cycle_id || !form.title || !form.scheduled_at) {
      alert('Title, cycle, and scheduled date/time are required'); return;
    }
    setSaving(true);
    try {
      if (form.id) await updateGASSession(form.id, form);
      else         await createGASSession(form);
      setForm(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving GAS session');
    } finally {
      setSaving(false);
    }
  };

  const remove = async s => {
    if (!window.confirm(`Archive the GAS session "${s.title}"?`)) return;
    try { await deleteGASSession(s.id); await load(); }
    catch (err) { alert(err.response?.data?.error || 'Error archiving'); }
  };

  const grouped = useMemo(() => ({
    scheduled: sessions.filter(s => s.status === 'scheduled'),
    complete:  sessions.filter(s => s.status === 'complete'),
    cancelled: sessions.filter(s => s.status === 'cancelled'),
  }), [sessions]);

  // Candidate participants: Sales program users + facilitators/managers outside
  const candidateUsers = useMemo(() =>
    users.filter(u => (Number(u.program_id) === 2 || ['admin','manager'].includes(u.role)))
  , [users]);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <PageHeader moduleKey="sales-gas" helpTask="sales-gas"
          subtitle="Quarterly multi-stakeholder sessions for the Sales team. Held in the last fortnight of each quarter." />
        {isManager && <Btn size="sm" onClick={openNew}>+ Schedule GAS</Btn>}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Cycle:</span>
        <Select value={filterCycle} onChange={e => setFilterCycle(e.target.value)} style={{ width: 260 }}>
          <option value="">All cycles</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}{c.status === 'active' ? ' · Active' : ''}</option>)}
        </Select>
      </div>

      {['scheduled','complete','cancelled'].map(group => {
        const items = grouped[group];
        if (!items.length) return null;
        return (
          <div key={group} style={{ marginBottom: 24 }}>
            <SectionTitle>{group[0].toUpperCase() + group.slice(1)}</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
              {items.map(s => (
                <Card key={s.id}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{s.title}</span>
                        <Badge color={STATUS_COLOR[s.status] || 'gray'}>{s.status}</Badge>
                        {s.cycle_name && <Badge color="gray">{s.cycle_name}</Badge>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                        {new Date(s.scheduled_at).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {s.location && <> · {s.location}</>}
                        {s.facilitator_name && <> · Facilitator: <strong>{s.facilitator_name}</strong></>}
                      </div>
                      {s.agenda && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5 }}>Agenda: {s.agenda}</div>}
                      {(s.participants || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                          {s.participants.map(p => (
                            <span key={p.user_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 20, background: 'var(--gray-100)', fontSize: 11 }}>
                              <Avatar initials={p.avatar_initials} size={18} />
                              {p.name}
                              <span style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>· {p.participant_role}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {s.notes && (
                        <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--gray-50)', borderRadius: 6, borderLeft: '3px solid var(--navy-500)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
                          {s.notes}
                        </div>
                      )}
                    </div>
                    {isManager && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <Btn variant="secondary" size="sm" onClick={() => openEdit(s)}>Edit</Btn>
                        <Btn variant="ghost" size="sm" onClick={() => remove(s)} style={{ color: 'var(--red-500)' }}>✕</Btn>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {sessions.length === 0 && (
        <Card>
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-3)', fontSize: 13 }}>
            No GAS sessions yet.{isManager && ' Click "+ Schedule GAS" to create one.'}
          </div>
        </Card>
      )}

      {/* ── Schedule / edit modal ── */}
      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? `Edit — ${form.title || 'GAS'}` : 'Schedule GAS'} width={640}>
        {form && (
          <form onSubmit={submit}>
            <Field label="Title" required>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Q1 GAS · July–September planning" />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Cycle" required>
                <Select value={form.cycle_id} onChange={e => setForm(f => ({ ...f, cycle_id: e.target.value }))}>
                  <option value="">Select…</option>
                  {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label="Date & time" required>
                <Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Location">
                <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Manly boardroom / Teams" />
              </Field>
              <Field label="Facilitator">
                <Select value={form.facilitator_id} onChange={e => setForm(f => ({ ...f, facilitator_id: e.target.value }))}>
                  <option value="">—</option>
                  {candidateUsers.map(u => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` — ${u.job_title}` : ''}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Agenda">
              <Textarea rows={3} value={form.agenda} onChange={e => setForm(f => ({ ...f, agenda: e.target.value }))} placeholder="What the session will cover." />
            </Field>
            {form.id && (
              <Field label="Notes (captured during/after the session)">
                <Textarea rows={5} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Key discussion points, decisions, follow-ups." />
              </Field>
            )}
            {form.id && (
              <Field label="Status">
                <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="scheduled">Scheduled</option>
                  <option value="complete">Complete</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </Field>
            )}

            <SectionTitle>Participants</SectionTitle>
            <div style={{ marginTop: 6, marginBottom: 14, border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 12px', maxHeight: 220, overflowY: 'auto' }}>
              {candidateUsers.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No candidates found.</div>
              )}
              {candidateUsers.map(u => {
                const p = form.participants.find(x => String(x.user_id) === String(u.id));
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '0.5px dashed var(--border)' }}>
                    <input type="checkbox" checked={!!p} onChange={() => toggleParticipant(u.id, 'panelist')} />
                    <Avatar initials={u.avatar_initials} size={22} />
                    <div style={{ flex: 1, fontSize: 12 }}>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                      <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{u.job_title || u.role}</div>
                    </div>
                    {p && (
                      <Select value={p.participant_role} onChange={e => setParticipantRole(u.id, e.target.value)} style={{ width: 140 }}>
                        <option value="emc">EMC</option>
                        <option value="panelist">Panelist</option>
                        <option value="observer">Observer</option>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" variant="secondary" onClick={() => setForm(null)}>Cancel</Btn>
              <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : form.id ? 'Save' : 'Schedule'}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
